import React, { useState, useEffect, useRef } from "react";
import {
  Mail,
  Phone,
  MapPin,
  GitBranch,
  Globe,
  ExternalLink,
  Briefcase,
  Terminal,
  Trophy,
  GraduationCap,
  Brain,
  Shield,
  MessageCircle,
  Ship,
  Zap,
  Activity,
  ChevronRight,
  ChevronDown,
  Server,
  CirclePlay,
} from "lucide-react";
import ycLogo from "./public/yc.png";
import shipdLogo from "./public/shipd.png";
import echoLogo from "./public/echo.png";
import deltaLogo from "./public/delta.png";
import digitalNirmanLogo from "./public/digitalnirman.png";
import cogneeLogo from "./public/cogne.png";
import mastraLogo from "./public/mastra.png";
import modelenceLogo from "./public/modelence.png";
import metaLogo from "./public/meta.png";
import openClawLogo from "./public/openclaw.png";
import shipSecLogo from "./public/shipsec.png";
import kiloCodeLogo from "./public/kilo.png";
import openaiLogo from "./public/openai.png";
import lamdaLogo from "./public/lamda.png";
import hermesLogo from "./public/hermes.png";
import modelRouterLogo from "./public/model-router.png";
import clickImage from "./public/click-animation/click.png";
import clarioLogo from "./public/clario.png";
import nagrikLogo from "./public/nagrik.png";
import esewaLogo from "./public/esewa.png";
import wwfLogo from "./public/wwf.png";
import bitsPilaniLogo from "./public/bitspilani.png";
import arnikoLogo from "./public/arniko.png";
import githubContrib from "./public/github-contribution.png";
import hackClubLogo from "./public/hack-club.png";
import insforgeLogo from "./public/insforge.png";
import fossasiaLogo from "./public/fossasia.png";
import questionIcon from "./public/question.png";
import { extractYouTubeId, youtubeThumb, youtubeEmbed } from "./lib/youtube";

type PortfolioVideo = { id: string; section: string; title: string; description?: string | null; destinationUrl?: string | null; sortOrder?: number };

// Language & Tech Stack Imports
import anchorLogo from "./public/language/anchor.png";
import crewaiLogo from "./public/language/CrewAI.png";
import dspyLogo from "./public/language/DSPy.png";
import fastapiLogo from "./public/language/fastapi.png";
import grpcLogo from "./public/language/grpc.png";
import huggingfaceLogo from "./public/language/HuggingFaceSuite.png";
import langchainLogo from "./public/language/langchain.png";
import llamaindexLogo from "./public/language/LlamaIndex.png";
import pgvectorLogo from "./public/language/pgvector.png";
import pytorchLogo from "./public/language/pytorch.png";
import rustLogo from "./public/language/rust.png";
import typescriptLogo from "./public/language/typescript.png";
import vllmLogo from "./public/language/vLLM.png";
import kubernetesLogo from "./public/language/kubernetes.png";
import javaLogo from "./public/language/java.png";
import reactLogo from "./public/language/react.png";
import redisLogo from "./public/language/redis.png";
import dockerLogo from "./public/language/docker.png";

import "./index.css";

const leafLogos = [
  ycLogo,
  shipdLogo,
  echoLogo,
  deltaLogo,
  digitalNirmanLogo,
  cogneeLogo,
  mastraLogo,
  modelenceLogo,
  metaLogo,
  openClawLogo,
  shipSecLogo,
  kiloCodeLogo,
  openaiLogo,
  lamdaLogo,
  hermesLogo,
  modelRouterLogo,
  clarioLogo,
  nagrikLogo,
  esewaLogo,
  wwfLogo,
  bitsPilaniLogo,
  arnikoLogo,
  hackClubLogo,
  insforgeLogo,
  fossasiaLogo,
  anchorLogo,
  crewaiLogo,
  dspyLogo,
  fastapiLogo,
  grpcLogo,
  huggingfaceLogo,
  langchainLogo,
  llamaindexLogo,
  pgvectorLogo,
  pytorchLogo,
  rustLogo,
  typescriptLogo,
  vllmLogo,
  kubernetesLogo,
  javaLogo,
  reactLogo,
  redisLogo,
  dockerLogo,
  clickImage,
];

interface LeafParticle {
  id: number;
  x: number;
  y: number;
  tx: string;
  ty: string;
  rot: string;
  src: string;
}

interface PhysicsParticle {
  id: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  rot: number;
  vrot: number;
  scale: number;
  opacity: number;
  decay: number;
  src: string;
  isBg: boolean;
}

export const globalAgentPositions: Record<string, { x: number, y: number }> = {};

const TechBadge = ({ tech, idx }: { tech: any; idx: number }) => {
  const elementRef = useRef<HTMLDivElement>(null);
  
  // High-performance direct-DOM physics states
  const posRef = useRef({ x: 0, y: 0 });
  const velRef = useRef({ x: 0, y: 0 });
  const rotRef = useRef(0);
  const rotVelRef = useRef(0);
  
  const mouseRef = useRef({ x: -1000, y: -1000 });

  useEffect(() => {
    const handleGlobalMouseMove = (e: MouseEvent) => {
      mouseRef.current = { x: e.clientX, y: e.clientY };
    };

    window.addEventListener("mousemove", handleGlobalMouseMove);
    return () => window.removeEventListener("mousemove", handleGlobalMouseMove);
  }, []);

  useEffect(() => {
    let animationFrameId: number;

    const floatSpeedX = 0.0006 + (idx % 3) * 0.0003;
    const floatSpeedY = 0.0005 + (idx % 2) * 0.0002;
    const floatAmpX = 2.5 + (idx % 2) * 1.0;
    const floatAmpY = 1.5 + (idx % 3) * 1.0;

    const tick = (time: number) => {
      if (!elementRef.current) {
        animationFrameId = requestAnimationFrame(tick);
        return;
      }

      const rect = elementRef.current.getBoundingClientRect();

      let ax = 0;
      let ay = 0;
      let arot = 0;

      const badgeX = rect.left + rect.width / 2;
      const badgeY = rect.top + rect.height / 2;
      const repulsionRadius = 100; // soft and gentle radius

      const applyRepulsion = (cx: number, cy: number) => {
        const dx = badgeX - cx;
        const dy = badgeY - cy;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < repulsionRadius && dist > 0) {
          const normalizedDist = dist / repulsionRadius;
          const force = Math.pow(1.0 - normalizedDist, 2.0) * 1.8; 
          ax += (dx / dist) * force;
          ay += (dy / dist) * force;
          arot += (dx > 0 ? 1 : -1) * force * 1.0;
        }
      };

      // Real mouse repulsion
      applyRepulsion(mouseRef.current.x, mouseRef.current.y);

      // Agent cursors repulsion (convert absolute document coords to viewport coords)
      Object.values(globalAgentPositions).forEach(pos => {
        applyRepulsion(pos.x - window.scrollX, pos.y - window.scrollY);
      });

      // Spring pulling back to Home drifting orbit
      const k = 0.05; // elastic spring constant
      const targetX = Math.sin(time * floatSpeedX) * floatAmpX;
      const targetY = Math.cos(time * floatSpeedY) * floatAmpY;

      const springX = (targetX - posRef.current.x) * k;
      const springY = (targetY - posRef.current.y) * k;
      const springRot = -rotRef.current * k;

      ax += springX;
      ay += springY;
      arot += springRot;

      // Damping / Friction
      const damping = 0.88; 
      velRef.current.x = (velRef.current.x + ax) * damping;
      velRef.current.y = (velRef.current.y + ay) * damping;
      rotVelRef.current = (rotVelRef.current + arot) * 0.82;

      posRef.current.x += velRef.current.x;
      posRef.current.y += velRef.current.y;
      rotRef.current += rotVelRef.current;

      // Safe, tight visual limits to prevent adjacent card overlapping
      const maxLimitX = 12;
      const maxLimitY = 10;
      posRef.current.x = Math.max(-maxLimitX, Math.min(maxLimitX, posRef.current.x));
      posRef.current.y = Math.max(-maxLimitY, Math.min(maxLimitY, posRef.current.y));

      // Direct DOM update
      elementRef.current.style.transform = `translate(${posRef.current.x}px, ${posRef.current.y}px) rotate(${rotRef.current}deg)`;

      animationFrameId = requestAnimationFrame(tick);
    };

    animationFrameId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(animationFrameId);
  }, [idx]);

  return (
    <div
      ref={elementRef}
      className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-zinc-800/80 bg-zinc-900/20 hover:border-indigo-500/50 hover:bg-zinc-900/40 cursor-pointer transition-colors duration-300 group select-none"
    >
      {tech.icon ? (
        <tech.icon className="h-5 w-5 text-indigo-400 group-hover:text-indigo-300 transition-colors" />
      ) : (
        <img 
          src={tech.logo} 
          alt={tech.name} 
          className={`h-5 w-5 object-contain filter group-hover:brightness-110 transition-transform duration-300 ${
            tech.name === "DSPy" || tech.name === "vLLM" ? "scale-[1.45]" : ""
          }`} 
        />
      )}
      <span className="text-sm font-medium text-zinc-300 group-hover:text-zinc-100 transition-colors">{tech.name}</span>
    </div>
  );
};

const ContribBadge = ({ logo, href, label, yc, idx }: { logo: string; href: string; label: string; yc?: string; idx: number }) => {
  const elementRef = useRef<HTMLDivElement>(null);
  
  const posRef = useRef({ x: 0, y: 0 });
  const velRef = useRef({ x: 0, y: 0 });
  const rotRef = useRef(0);
  const rotVelRef = useRef(0);
  
  const mouseRef = useRef({ x: -1000, y: -1000 });

  useEffect(() => {
    const handleGlobalMouseMove = (e: MouseEvent) => {
      mouseRef.current = { x: e.clientX, y: e.clientY };
    };

    window.addEventListener("mousemove", handleGlobalMouseMove);
    return () => window.removeEventListener("mousemove", handleGlobalMouseMove);
  }, []);

  useEffect(() => {
    let animationFrameId: number;

    const floatSpeedX = 0.0006 + (idx % 3) * 0.0003;
    const floatSpeedY = 0.0005 + (idx % 2) * 0.0002;
    const floatAmpX = 2.0 + (idx % 2) * 1.0;
    const floatAmpY = 1.0 + (idx % 3) * 1.0;
    const rotSpeed = 0.0003 + (idx % 2) * 0.0002;
    const rotAmp = 1.0 + (idx % 3) * 0.8;

    const tick = (time: number) => {
      if (!elementRef.current) {
        animationFrameId = requestAnimationFrame(tick);
        return;
      }

      const rect = elementRef.current.getBoundingClientRect();

      let ax = 0;
      let ay = 0;
      let arot = 0;

      const badgeX = rect.left + rect.width / 2;
      const badgeY = rect.top + rect.height / 2;
      const repulsionRadius = 100;

      const applyRepulsion = (cx: number, cy: number) => {
        const dx = badgeX - cx;
        const dy = badgeY - cy;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < repulsionRadius && dist > 0) {
          const normalizedDist = dist / repulsionRadius;
          const force = Math.pow(1.0 - normalizedDist, 2.0) * 1.8; 
          ax += (dx / dist) * force;
          ay += (dy / dist) * force;
          arot += (dx > 0 ? 1 : -1) * force * 1.0;
        }
      };

      applyRepulsion(mouseRef.current.x, mouseRef.current.y);
      Object.values(globalAgentPositions).forEach(pos => {
        applyRepulsion(pos.x - window.scrollX, pos.y - window.scrollY);
      });

      const k = 0.05;
      const targetX = Math.sin(time * floatSpeedX) * floatAmpX;
      const targetY = Math.cos(time * floatSpeedY) * floatAmpY;

      const springX = (targetX - posRef.current.x) * k;
      const springY = (targetY - posRef.current.y) * k;
      const springRot = -rotRef.current * k;

      ax += springX;
      ay += springY;
      arot += springRot;

      const damping = 0.88; 
      velRef.current.x = (velRef.current.x + ax) * damping;
      velRef.current.y = (velRef.current.y + ay) * damping;
      rotVelRef.current = (rotVelRef.current + arot) * 0.82;

      posRef.current.x += velRef.current.x;
      posRef.current.y += velRef.current.y;
      rotRef.current += rotVelRef.current;

      const maxLimitX = 8;
      const maxLimitY = 6;
      posRef.current.x = Math.max(-maxLimitX, Math.min(maxLimitX, posRef.current.x));
      posRef.current.y = Math.max(-maxLimitY, Math.min(maxLimitY, posRef.current.y));

      elementRef.current.style.transform = `translate(${posRef.current.x}px, ${posRef.current.y}px) rotate(${rotRef.current}deg)`;

      animationFrameId = requestAnimationFrame(tick);
    };

    animationFrameId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(animationFrameId);
  }, [idx]);

  return (
    <div
      ref={elementRef}
      className="inline-flex items-center gap-1.5 text-sm border border-zinc-700/50 rounded-md px-2 py-1 bg-zinc-900/10 hover:border-indigo-500/50 hover:bg-zinc-900/40 cursor-pointer transition-colors duration-300 select-none group/contrib"
    >
      <img src={logo} alt={label} className="h-4 w-4 rounded-sm" />
      <a href={href} target="_blank" rel="noreferrer" className="font-medium text-zinc-200 group-hover/contrib:text-indigo-300 transition-colors">
        {label}
      </a>
      {yc && (
        <span className="inline-flex items-center gap-0.5 font-bold text-[#FF6600] text-xs">
          <img src={ycLogo} alt="YC" className="h-3 w-3" />
          {yc}
        </span>
      )}
    </div>
  );
};

const AGENTS = [
  { name: "Claude", color: "#f97316" },
  { name: "Codex", color: "#3b82f6" },
  { name: "Pi", color: "#10b981" },
];

const AgentCursor = ({ name, color }: { name: string; color: string }) => {
  const cursorRef = useRef<HTMLDivElement>(null);
  
  const getBounds = () => {
    if (typeof document !== 'undefined') {
      return {
        w: document.documentElement.scrollWidth,
        h: document.documentElement.scrollHeight
      };
    }
    return { w: typeof window !== 'undefined' ? window.innerWidth : 1000, h: typeof window !== 'undefined' ? window.innerHeight : 1000 };
  };

  const initialBounds = getBounds();

  const posRef = useRef({ x: Math.random() * initialBounds.w, y: Math.random() * initialBounds.h });
  const targetRef = useRef({ x: Math.random() * initialBounds.w, y: Math.random() * initialBounds.h });
  const velRef = useRef({ x: 0, y: 0 });

  useEffect(() => {
    let animationFrameId: number;

    const tick = () => {
      if (!cursorRef.current) return;

      const bounds = getBounds();

      // Check if their current target is outside the user's view
      const targetIsOutOfView = targetRef.current.y < window.scrollY || targetRef.current.y > window.scrollY + window.innerHeight;

      // Update target IMMEDIATELY if you scrolled away, otherwise update very rarely (smooth ambient movement)
      if (targetIsOutOfView || Math.random() < 0.005) {
        // Simple logic: pick either the left 15% or right 15% of the screen
        const margin = bounds.w * 0.15;
        const isLeft = Math.random() > 0.5;
        const targetX = isLeft ? Math.random() * margin : bounds.w - margin + Math.random() * (margin - 100);

        targetRef.current = {
          x: targetX,
          y: window.scrollY + Math.random() * (window.innerHeight - 100),
        };
      }

      const dx = targetRef.current.x - posRef.current.x;
      const dy = targetRef.current.y - posRef.current.y;
      
      // Gentle pull
      const k = 0.001; 
      let ax = dx * k;
      let ay = dy * k;

      // High friction for smooth, gliding movement
      const damping = 0.94; 
      velRef.current.x = (velRef.current.x + ax) * damping;
      velRef.current.y = (velRef.current.y + ay) * damping;

      // Almost unnoticeable random drift
      velRef.current.x += (Math.random() - 0.5) * 0.02;
      velRef.current.y += (Math.random() - 0.5) * 0.02;

      // Fast max velocity so they don't lag behind when scrolling fast, but rarely hit this speed ambiently
      const maxVel = 12.0;
      velRef.current.x = Math.max(-maxVel, Math.min(maxVel, velRef.current.x));
      velRef.current.y = Math.max(-maxVel, Math.min(maxVel, velRef.current.y));

      posRef.current.x += velRef.current.x;
      posRef.current.y += velRef.current.y;

      const maxX = bounds.w - 20;
      const maxY = bounds.h - 20;
      
      if (posRef.current.x < 0) { posRef.current.x = 0; velRef.current.x *= -1; }
      if (posRef.current.x > maxX) { posRef.current.x = maxX; velRef.current.x *= -1; }
      if (posRef.current.y < 0) { posRef.current.y = 0; velRef.current.y *= -1; }
      if (posRef.current.y > maxY) { posRef.current.y = maxY; velRef.current.y *= -1; }

      globalAgentPositions[name] = { x: posRef.current.x, y: posRef.current.y };

      cursorRef.current.style.transform = `translate(${posRef.current.x}px, ${posRef.current.y}px)`;

      animationFrameId = requestAnimationFrame(tick);
    };

    animationFrameId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(animationFrameId);
  }, []);

  return (
    <div
      ref={cursorRef}
      className="absolute top-0 left-0 z-[100] pointer-events-none flex items-start gap-1.5 drop-shadow-lg"
      style={{ willChange: 'transform' }}
    >
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        style={{ transform: 'rotate(-15deg)', color }}
      >
        <path
          d="M7 2L20.8906 13.6265C21.849 14.4286 21.2828 15.986 20.0381 16.037L14.7731 16.2555L11.7588 21.4646C11.1396 22.5348 9.53986 22.3995 9.15579 21.2464L7 2Z"
          fill="currentColor"
          stroke="white"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
      </svg>
      <div
        className="px-2 py-0.5 rounded-full text-white text-[10px] font-bold whitespace-nowrap shadow-md mt-3 backdrop-blur-md"
        style={{ backgroundColor: color + '4d' }}
      >
        {name} working...
      </div>
    </div>
  );
};

type SiteNotice = { enabled: boolean; message: string; url?: string; highlightColor?: string; startAt?: string | null; expiresAt?: string | null; from?: string; to?: string };

function toPlace(input?: string): { label: string; point: [number, number] } | null {
  if (!input) return null;
  const t = input.trim();
  const label = t;
  const coords = t.match(/^(-?\d+(?:\.\d+)?)\s*[,/]\s*(-?\d+(?:\.\d+)?)$/);
  if (coords) return { label, point: [parseFloat(coords[1] ?? ""), parseFloat(coords[2] ?? "")] };
  const places: Record<string, [number, number]> = {
    nepal: [27.7172, 85.324], kathmandu: [27.7172, 85.324], "san francisco": [37.7749, -122.4194], sf: [37.7749, -122.4194],
    "new york": [40.7128, -74.006], ny: [40.7128, -74.006], london: [51.5074, -0.1278], paris: [48.8566, 2.3522],
    berlin: [52.52, 13.405], amsterdam: [52.3676, 4.9041], tokyo: [35.6762, 139.6503], seoul: [37.5665, 126.978],
    singapore: [1.3521, 103.8198], "kuala lumpur": [3.139, 101.6869], bangkok: [13.7563, 100.5018],
    sydney: [-33.8688, 151.2093], melbourne: [-37.8136, 144.9631], delhi: [28.6139, 77.209], "new delhi": [28.6139, 77.209],
    bengaluru: [12.9716, 77.5946], bangalore: [12.9716, 77.5946], hyderabad: [17.385, 78.4867], mumbai: [19.076, 72.8777],
    dhaka: [23.8103, 90.4125], kolkata: [22.5726, 88.3639], colombo: [6.9271, 79.8612], dubai: [25.2048, 55.2708],
    doha: [25.2854, 51.531], qatar: [25.2854, 51.531], istanbul: [41.0082, 28.9784], moscow: [55.7558, 37.6173],
    "los angeles": [34.0522, -118.2437], la: [34.0522, -118.2437], seattle: [47.6062, -122.3321], austin: [30.2672, -97.7431],
    chicago: [41.8781, -87.6298], denver: [39.7392, -104.9903], houston: [29.7604, -95.3698],
    toronto: [43.6532, -79.3832], vancouver: [49.2827, -123.1207], taipei: [25.033, 121.5654],
    "hong kong": [22.3193, 114.1694], manila: [14.5995, 120.9842],
  };
  const tl = t.toLowerCase();
  if (places[tl]) return { label, point: places[tl] };
  const words = tl.split(/\s+/);
  for (let w = words.length; w >= 1; w--) {
    for (let i = 0; i <= words.length - w; i++) {
      const key = words.slice(i, i + w).join(" ");
      if (places[key]) return { label, point: places[key] };
    }
  }
  return null;
}

type V3 = [number, number, number];
function toVec(lat: number, lon: number): V3 {
  const φ = (lat * Math.PI) / 180, λ = (lon * Math.PI) / 180, c = Math.cos(φ);
  return [c * Math.cos(λ), Math.sin(φ), c * Math.sin(λ)];
}
function slerp(a: V3, b: V3, t: number): V3 {
  let dot = a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
  dot = Math.max(-1, Math.min(1, dot));
  const θ = Math.acos(dot);
  if (θ < 1e-6) return a;
  const s = Math.sin(θ), ka = Math.sin((1 - t) * θ) / s, kb = Math.sin(t * θ) / s;
  const r: V3 = [ka * a[0] + kb * b[0], ka * a[1] + kb * b[1], ka * a[2] + kb * b[2]];
  const n = Math.hypot(...r);
  return [r[0] / n, r[1] / n, r[2] / n];
}

function RouteGlobe({ from, to, tint }: { from?: { label?: string; point: [number, number] } | null; to?: { label?: string; point: [number, number] } | null; tint: string }) {
  const R = 34, tilt = 18;
  const fromVec = from?.point ? toVec(from.point[0], from.point[1]) : null;
  const toVecPoint = to?.point ? toVec(to.point[0], to.point[1]) : null;
  const hasRoute = !!fromVec && !!toVecPoint;
  const mid = hasRoute && fromVec && toVecPoint ? slerp(fromVec, toVecPoint, 0.5) : (fromVec || toVecPoint || toVec(22, 0));
  const yaw = -Math.atan2(mid[0], mid[2]);
  const cosYaw = Math.cos(yaw), sinYaw = Math.sin(yaw);
  const cosTilt = Math.cos((tilt * Math.PI) / 180), sinTilt = Math.sin((tilt * Math.PI) / 180);

  const pt = (v: V3) => {
    const x1 = v[0] * cosYaw + v[2] * sinYaw;
    const z1 = -v[0] * sinYaw + v[2] * cosYaw;
    const y2 = v[1] * cosTilt - z1 * sinTilt;
    const z2 = v[1] * sinTilt + z1 * cosTilt;
    return { x: x1 * R, y: -y2 * R, z: z2 };
  };

  const buildPath = (pts: (V3 | [number, number])[]) => {
    let d = ""; let prev = false;
    for (const p of pts) {
      const v = (Array.isArray(p) && p.length === 2 ? toVec(p[0], p[1]) : p) as V3;
      const q = pt(v);
      const vis = q.z > 0;
      if (vis && !prev) d += `M${q.x.toFixed(1)} ${q.y.toFixed(1)}`;
      if (vis) d += `L${q.x.toFixed(1)} ${q.y.toFixed(1)}`;
      prev = vis;
    }
    return d;
  };

  const parallels: [number, number][] = [];
  for (let lat = -60; lat <= 60; lat += 25) for (let lon = -180; lon <= 180; lon += 10) parallels.push([lat, lon]);
  const meridians: [number, number][] = [];
  for (let lon = 0; lon < 360; lon += 30) for (let lat = -82; lat <= 82; lat += 6) meridians.push([lat, lon]);
  const graticule = buildPath(parallels) + buildPath(meridians);

  const arcPts = hasRoute && fromVec && toVecPoint
    ? Array.from({ length: 96 }, (_, i) => slerp(fromVec, toVecPoint, i / 95))
    : null;
  const arcPath = arcPts ? buildPath(arcPts) : "";

  const [progress, setProgress] = useState(0);
  useEffect(() => {
    if (!arcPts) return;
    let raf = 0; const t0 = performance.now();
    const step = (now: number) => {
      setProgress(((now - t0) / 6000) % 1);
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [hasRoute]);

  const comet = arcPts && arcPts.length ? pt(arcPts[Math.floor(progress * (arcPts.length - 1))] ?? arcPts[0]!) : null;
  const start = fromVec ? pt(fromVec) : null;
  const end = toVecPoint ? pt(toVecPoint) : null;

  const marker = (q: { x: number; y: number; z: number } | null, key: string) =>
    !q || q.z <= 0 ? null : (
      <g key={key}>
        <circle cx={q.x} cy={q.y} r={3.2} fill={tint} />
        <circle cx={q.x} cy={q.y} r={3.2} fill="none" stroke={tint} strokeWidth={0.7} className="globe-ping" />
      </g>
    );

  return (
    <svg viewBox="-44 -44 88 88" className="globe-drift h-14 w-14 shrink-0 sm:h-20 sm:w-20" role="img" aria-label="Route map">
      <defs>
        <radialGradient id="globe-shade" cx="35%" cy="30%" r="80%">
          <stop offset="0%" stopColor={tint} stopOpacity="0.35" />
          <stop offset="55%" stopColor="#18181b" stopOpacity="0.9" />
          <stop offset="100%" stopColor="#09090b" stopOpacity="1" />
        </radialGradient>
        <filter id="globe-glow" x="-60%" y="-60%" width="220%" height="220%">
          <feGaussianBlur stdDeviation="2.2" />
        </filter>
      </defs>
      <circle cx="0" cy="0" r={R} fill="url(#globe-shade)" stroke={tint + "44"} strokeWidth="0.8" />
      <path d={graticule} fill="none" stroke={tint + "59"} strokeWidth="0.45" strokeLinecap="round" />
      {arcPath && (
        <>
          <path d={arcPath} fill="none" stroke={tint} strokeOpacity="0.25" strokeWidth="0.9" />
          <path d={arcPath} fill="none" stroke={tint} strokeWidth="1.6" strokeLinecap="round" strokeDasharray="3 7" className="globe-flow" />
        </>
      )}
      {comet && hasRoute && (
        <g transform={`translate(${comet.x} ${comet.y})`} filter="url(#globe-glow)">
          <circle r={4.6} fill={tint} opacity="0.5" />
          <circle r={2.4} fill="#fff" />
        </g>
      )}
      {marker(start, "start")}
      {marker(end, "end")}
    </svg>
  );
}

function NoticeToast({ notice }: { notice: SiteNotice }) {
  const startMs = notice.startAt ? Date.parse(notice.startAt) : 0;
  const expireMs = notice.expiresAt ? Date.parse(notice.expiresAt) : Number.POSITIVE_INFINITY;
  const [nowMs, setNowMs] = useState(Date.now());
  const [dismissed, setDismissed] = useState(false);
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    const fadeTimer = setTimeout(() => setExiting(true), 2600);
    return () => clearTimeout(fadeTimer);
  }, []);

  useEffect(() => {
    if (!exiting) return;
    const t = setTimeout(() => setDismissed(true), 400);
    return () => clearTimeout(t);
  }, [exiting]);

  useEffect(() => {
    const targets = [startMs, expireMs].filter(t => Number.isFinite(t) && t > Date.now());
    if (!targets.length) return;
    const timers = targets.map(t => setTimeout(() => setNowMs(Date.now()), t - Date.now()));
    return () => { timers.forEach(clearTimeout); };
  }, [startMs, expireMs, nowMs]);

  const active = notice.enabled && !!notice.message && nowMs >= startMs && nowMs < expireMs;
  if (!active || dismissed) return null;

  const color = notice.highlightColor || "#6366f1";
  const from = toPlace(notice.from);
  const to = toPlace(notice.to);
  const hasRoute = !!from && !!to;

  return (
    <div className={`notice-pop fixed bottom-4 left-1/2 z-[55] w-[94vw] max-w-lg ${exiting ? "notice-exit" : ""}`} style={{ borderRadius: "1.25rem" }}>
      <div className="relative overflow-hidden rounded-2xl border bg-zinc-900/95 p-3.5 shadow-2xl backdrop-blur-md sm:p-4" style={{ borderColor: color + "66", boxShadow: `0 16px 48px ${color}2b` }}>
        <div className="pointer-events-none absolute -inset-x-8 -top-16 h-32 rounded-full opacity-50 blur-3xl" style={{ background: `radial-gradient(circle, ${color}26, transparent 65%)` }} />
        <button onClick={() => setExiting(true)} className="absolute right-2.5 top-2.5 z-10 rounded-lg p-1 text-zinc-500 transition-colors hover:bg-white/10 hover:text-white" aria-label="Dismiss">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
        </button>
        <div className="flex items-center gap-3 sm:gap-4">
          <RouteGlobe from={from} to={to} tint={color} />
          <div className="min-w-0 flex-1">
            <p className="text-[9px] font-bold uppercase tracking-[0.25em]" style={{ color: color + "bb" }}>Notice</p>
            {notice.url ? (
              <a href={notice.url} target="_blank" rel="noopener noreferrer" className="mt-0.5 block text-sm font-semibold leading-snug text-white underline decoration-zinc-600 underline-offset-2 hover:text-indigo-200 sm:text-base">{notice.message}</a>
            ) : (
              <p className="mt-0.5 text-sm font-semibold leading-snug text-white sm:text-base">{notice.message}</p>
            )}
            {hasRoute && (
              <span className="mt-1 inline-flex items-center gap-1 rounded-full border border-zinc-700/60 bg-black/40 px-2 py-px text-[10px] font-semibold tracking-wide text-zinc-400">
                {from!.label} <svg xmlns="http://www.w3.org/2000/svg" width="9" height="9" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg> {to!.label}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export function App() {
  const [leaves, setLeaves] = useState<LeafParticle[]>([]);
  const [bgLeaves, setBgLeaves] = useState<LeafParticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [fading, setFading] = useState(false);
  const [videos, setVideos] = useState<PortfolioVideo[]>([]);
  const [notice, setNotice] = useState<SiteNotice | null>(null);

  useEffect(() => {
    const apiBase = (window.__PORTFOLIO_CONFIG__?.apiUrl ?? "").replace(/\/$/, "");
    let cancelled = false;
    const apply = (data: { items?: PortfolioVideo[]; settings?: Record<string, any> }) => {
      if (cancelled) return;
      setVideos((data.items ?? []).filter((item: PortfolioVideo) => item.section === "VIDEO"));
      const n = data.settings?.notice;
      setNotice(n?.enabled && n?.message ? n : null);
    };
    try {
      const cached = localStorage.getItem("portfolio-content");
      if (cached) apply(JSON.parse(cached));
    } catch {}
    fetch("/portfolio-content.json")
      .then(response => response.ok ? response.json() : null)
      .then(data => { if (data) apply(data); })
      .catch(() => {});
    fetch(`${apiBase}/api/public/content`)
      .then(response => { if (!response.ok) throw new Error("Failed to load public content"); return response.json(); })
      .then(data => {
        apply(data);
        try { localStorage.setItem("portfolio-content", JSON.stringify(data)); } catch {}
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setFading(true), 1000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (fading) {
      const timer = setTimeout(() => setLoading(false), 300);
      return () => clearTimeout(timer);
    }
  }, [fading]);

  // Event listeners for Click and MouseMove
  useEffect(() => {
    const playClickSound = () => {
      try {
        const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
        if (!AudioContext) return;
        
        const ctx = new AudioContext();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        
        osc.connect(gain);
        gain.connect(ctx.destination);
        
        osc.type = 'sine';
        osc.frequency.setValueAtTime(600, ctx.currentTime);
        osc.frequency.exponentialRampToValueAtTime(300, ctx.currentTime + 0.1);
        
        gain.gain.setValueAtTime(0.1, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.1);
        
        osc.start(ctx.currentTime);
        osc.stop(ctx.currentTime + 0.1);
      } catch (err) {
        // Ignore audio errors
      }
    };

    const handleClick = (e: MouseEvent) => {
      playClickSound();
      
      const numLeaves = Math.floor(Math.random() * 3) + 4; // original 4-6 leaves
      const newLeaves: LeafParticle[] = [];
      const idPrefix = Date.now();
      
      for (let i = 0; i < numLeaves; i++) {
        const angle = Math.random() * Math.PI * 2;
        const distance = Math.random() * 50 + 30;
        const tx = Math.cos(angle) * distance;
        const ty = Math.sin(angle) * distance;
        const rot = Math.random() * 360; 
        
        newLeaves.push({
          id: idPrefix + i,
          x: e.clientX,
          y: e.clientY,
          tx: `${tx}px`,
          ty: `${ty}px`,
          rot: `${rot}deg`,
          src: leafLogos[Math.floor(Math.random() * leafLogos.length)],
        });
      }
      
      setLeaves(prev => [...prev, ...newLeaves]);
      
      setTimeout(() => {
        setLeaves(prev => prev.filter(leaf => leaf.id < idPrefix || leaf.id >= idPrefix + numLeaves));
      }, 800);
    };

    let lastMove = 0;

    const handleMouseMove = (e: MouseEvent) => {
      const now = Date.now();
      if (now - lastMove < 50) return; // original 50ms throttle
      lastMove = now;

      const count = Math.floor(Math.random() * 2) + 1; // original 1-2 trailing leaves
      const newLeaves: LeafParticle[] = [];
      const idPrefix = now;
      
      for (let i = 0; i < count; i++) {
        const angle = Math.random() * Math.PI * 2;
        const distance = Math.random() * 30 + 15;
        const tx = Math.cos(angle) * distance;
        const ty = Math.sin(angle) * distance;
        const rot = Math.random() * 360;
        
        newLeaves.push({
          id: idPrefix + i,
          x: e.clientX,
          y: e.clientY,
          tx: `${tx}px`,
          ty: `${ty}px`,
          rot: `${rot}deg`,
          src: leafLogos[Math.floor(Math.random() * leafLogos.length)],
        });
      }
      
      setLeaves(prev => [...prev, ...newLeaves]);
      
      setTimeout(() => {
        setLeaves(prev => prev.filter(leaf => leaf.id < idPrefix || leaf.id >= idPrefix + count));
      }, 800);
    };

    window.addEventListener("click", handleClick);
    window.addEventListener("mousemove", handleMouseMove);

    return () => {
      window.removeEventListener("click", handleClick);
      window.removeEventListener("mousemove", handleMouseMove);
    };
  }, []);

  // Background slow CSS drift leaves
  useEffect(() => {
    const spawnBgLeaf = () => {
      const angle = Math.random() * Math.PI * 2;
      const distance = Math.random() * 200 + 100;
      const tx = Math.cos(angle) * distance;
      const ty = Math.sin(angle) * distance;
      const rot = Math.random() * 720 - 360;
      const duration = Math.random() * 6 + 6;

      const leaf: LeafParticle = {
        id: Date.now() + Math.random(),
        x: Math.random() * window.innerWidth,
        y: Math.random() * window.innerHeight,
        tx: `${tx}px`,
        ty: `${ty}px`,
        rot: `${rot}deg`,
        src: leafLogos[Math.floor(Math.random() * leafLogos.length)],
      };

      setBgLeaves(prev => [...prev, leaf]);

      setTimeout(() => {
        setBgLeaves(prev => prev.filter(l => l.id !== leaf.id));
      }, duration * 1000);
    };

    const interval = setInterval(spawnBgLeaf, 800);

    return () => clearInterval(interval);
  }, []);

  return (
    <>
      {loading && (
        <div className={`fixed inset-0 z-50 flex items-center justify-center bg-black transition-opacity duration-300 ${fading ? "opacity-0" : "opacity-100"}`}>
          <div className="relative flex items-center justify-center gap-3">
            <div className="absolute w-32 h-32 bg-indigo-500/30 rounded-full blur-3xl animate-pulse" />
            <span className="text-2xl font-bold text-zinc-300 animate-spin relative z-10">cool</span>
            <img
              src={clickImage}
              alt="Loading"
              className="w-12 h-12 animate-spin relative z-10"
            />
            <span className="text-2xl font-bold text-zinc-300 animate-spin relative z-10">cool</span>
          </div>
        </div>
      )}
      {notice && <NoticeToast notice={notice} />}
      <div className={loading ? "hidden" : ""}>
      <div className="relative min-h-screen overflow-x-hidden bg-black text-zinc-300 font-sans selection:bg-indigo-500/30 selection:text-indigo-200">
      
      {/* Agent Cursors */}
      {!loading && AGENTS.map((agent, i) => (
        <AgentCursor key={i} name={agent.name} color={agent.color} />
      ))}
      
      {/* Background Floating Logos */}
      {bgLeaves.map(leaf => (
        <div
          key={leaf.id}
          className="bg-leaf-particle"
          style={{
            left: leaf.x,
            top: leaf.y,
            '--tx': leaf.tx,
            '--ty': leaf.ty,
            '--rot': leaf.rot,
            '--duration': `${leaf.id % 10 + 6}s`,
          } as React.CSSProperties}
        >
          <img src={leaf.src} alt="" className="w-6 h-6 opacity-20" />
        </div>
      ))}

      {/* Leaf Animation */}
      {leaves.map(leaf => (
        <div
          key={leaf.id}
          className="leaf-particle"
          style={{
            left: leaf.x,
            top: leaf.y,
            '--tx': leaf.tx,
            '--ty': leaf.ty,
            '--rot': leaf.rot,
          } as React.CSSProperties}
        >
          <img src={leaf.src} alt="" className="w-6 h-6 opacity-15" />
        </div>
      ))}

      {/* Background */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] bg-yellow-500/10 rounded-full blur-[120px]" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] bg-amber-600/10 rounded-full blur-[120px]" />
      </div>

      <div className="relative z-10 mx-auto min-h-screen max-w-3xl px-6 py-12 md:px-12 md:py-20 lg:py-24">
        <div className="flex flex-col gap-16">
          
          {/* Header */}
          <header className="flex flex-col items-center text-center">
            <div>
              <h1 className="text-4xl font-bold tracking-tight sm:text-5xl bg-gradient-to-r from-white via-zinc-300 to-white bg-clip-text text-transparent">
                Aaditya Sah
              </h1>
              <div className="mt-3 h-0.5 w-16 mx-auto rounded-full bg-gradient-to-r from-zinc-600 via-zinc-400 to-zinc-600" />
              <h2 className="mt-4 text-lg font-medium tracking-tight text-zinc-200 sm:text-xl">
                Backend & Applied AI Engineer
              </h2>
              <p className="mt-4 max-w-2xl leading-relaxed text-zinc-400">
                Ai applied engineer, core system engineer(Rust) & distributed systems
              </p>
            </div>
            </header>

          {/* Right Main Content */}
          <main className="space-y-24">
            
            {/* About Section */}
            <section id="about" className="scroll-mt-16 md:scroll-mt-24">
              <div className="mb-6">
                <h2 className="text-xl font-bold uppercase tracking-widest text-white">About</h2>
              </div>
              <div className="space-y-4 text-zinc-400 leading-relaxed">
                <p>
                  Currently contributing to production engineering at <span className="inline-flex items-center gap-1 font-medium text-zinc-200">Shipd (<span className="inline-flex items-center gap-1 font-bold text-[#FF6600]"><img src={ycLogo} alt="YC" className="h-4 w-4" />W24</span>)</span>, with hands-on experience in API design, <span className="font-medium text-zinc-200">System design</span>, real-time LLM features, data modelling, Docker, CI/CD, model routing, context management, and <span className="font-medium text-zinc-200">agent evaluation</span>.
                </p>
                <p>
                  I've built and maintained <a href="https://clario-well.pages.dev/" target="_blank" rel="noreferrer" className="font-medium text-zinc-200 hover:text-indigo-300 transition-all duration-200 hover:drop-shadow-[0_0_8px_rgba(99,102,241,0.6)]"><img src={clarioLogo} alt="Clario" className="h-5 w-5 inline align-middle rounded-sm" /><span className="ml-1">Clario</span></a>, serving <span className="font-medium text-zinc-200">5,000+ active users</span>. My expertise lies at the intersection of robust backend infrastructure and applied artificial intelligence, crafting reliable agent systems and APIs.
                </p>
              </div>
            </section>

            {/* Tech Stack Section */}
            <section id="tech-stack" className="scroll-mt-16 md:scroll-mt-24">
              <div className="mb-6">
                <h2 className="text-xl font-bold uppercase tracking-widest text-white">Tech Stack (19)</h2>
              </div>
              <div className="flex flex-wrap gap-2.5 ml-3">
                {[
                  { name: "Rust", logo: rustLogo },
                  { name: "TypeScript", logo: typescriptLogo },
                  { name: "FastAPI", logo: fastapiLogo },
                  { name: "gRPC", logo: grpcLogo },
                  { name: "pgvector", logo: pgvectorLogo },
                  { name: "LlamaIndex", logo: llamaindexLogo },
                  { name: "LangChain", logo: langchainLogo },
                  { name: "CrewAI", logo: crewaiLogo },
                  { name: "DSPy", logo: dspyLogo },
                  { name: "vLLM", logo: vllmLogo },
                  { name: "PyTorch", logo: pytorchLogo },
                  { name: "Hugging Face", logo: huggingfaceLogo },
                  { name: "Anchor", logo: anchorLogo },
                  { name: "Kubernetes", logo: kubernetesLogo },
                  { name: "Java", logo: javaLogo },
                  { name: "React", logo: reactLogo },
                  { name: "Redis", logo: redisLogo },
                  { name: "Docker", logo: dockerLogo },
                  { name: "System Design", icon: Server },
                ].map((tech, idx) => (
                  <TechBadge key={idx} tech={tech} idx={idx} />
                ))}
              </div>
            </section>

            {/* Experience Section */}
            <section id="experience" className="scroll-mt-16 md:scroll-mt-24">
              <div className="mb-8">
                <h2 className="text-xl font-bold uppercase tracking-widest text-white">Experience</h2>
              </div>
              
              <div className="group/list relative space-y-12 border-l border-zinc-800 ml-3">
                {/* Job 1 */}
                <div className="group relative grid pb-1 transition-all sm:grid-cols-8 sm:gap-8 md:gap-4 pl-8">
                  <div className="absolute -inset-x-4 -inset-y-4 z-0 hidden rounded-md transition motion-reduce:transition-none lg:-inset-x-6 lg:block lg:group-hover:bg-zinc-800/50 lg:group-hover:shadow-[inset_0_1px_0_0_rgba(148,163,184,0.1)] lg:group-hover:drop-shadow-lg"></div>
                  <div className="absolute -left-[6.5px] mt-1.5 h-3 w-3 rounded-full border-2 border-indigo-500 bg-black z-10"></div>
                  <header className="z-10 mb-2 mt-1 text-xs font-semibold uppercase tracking-wide text-zinc-500 sm:col-span-2">
                    2026 — Present
                  </header>
                  <div className="z-10 sm:col-span-6">
                    <h3 className="font-medium leading-snug text-zinc-200">
                      <div>
                        <a 
                          className="inline-flex items-center gap-1.5 text-base font-medium text-zinc-200 hover:text-indigo-300 transition-colors group/link cursor-pointer" 
                          href="https://shipd.ai/" 
                          target="_blank" 
                          rel="noreferrer"
                        >
                          <span className="absolute -inset-x-4 -inset-y-2.5 hidden rounded md:-inset-x-6 md:-inset-y-4 lg:block"></span>
                          <span>Software Engineer · <img src={shipdLogo} alt="Shipd" className="h-4 w-4 rounded-sm inline" /> <span className="inline-flex items-center gap-1 text-base font-medium">Shipd (<span className="inline-flex items-center gap-1 font-bold text-[#FF6600]"><img src={ycLogo} alt="YC" className="h-4 w-4" />W24</span>)</span></span>
                          <ExternalLink className="ml-1 inline-block h-3.5 w-3.5 shrink-0 translate-y-0.5 transition-transform group-hover/link:-translate-y-1 group-hover/link:translate-x-1" />
                        </a>
                      </div>
                    </h3>
                    <p className="mt-2 text-sm leading-normal text-zinc-400">
                      Contributing to production software engineering. Working across backend and applied-AI product development in a high-ownership startup environment.
                    </p>
                  </div>
                </div>

                {/* Job 2 */}
                <div className="group relative grid pb-1 transition-all sm:grid-cols-8 sm:gap-8 md:gap-4 pl-8">
                  <div className="absolute -inset-x-4 -inset-y-4 z-0 hidden rounded-md transition motion-reduce:transition-none lg:-inset-x-6 lg:block lg:group-hover:bg-zinc-800/50 lg:group-hover:shadow-[inset_0_1px_0_0_rgba(148,163,184,0.1)] lg:group-hover:drop-shadow-lg"></div>
                  <div className="absolute -left-[6.5px] mt-1.5 h-3 w-3 rounded-full border-2 border-indigo-500 bg-black z-10"></div>
                  <header className="z-10 mb-2 mt-1 text-xs font-semibold uppercase tracking-wide text-zinc-500 sm:col-span-2">
                    Jan — Jul 2026
                  </header>
                  <div className="z-10 sm:col-span-6">
                    <h3 className="font-medium leading-snug text-zinc-200">
                      <div>
                        <a 
                          className="inline-flex items-center gap-1.5 text-base font-medium text-zinc-200 hover:text-indigo-300 transition-colors group/link cursor-pointer" 
                          href="https://www.aiecho.live/" 
                          target="_blank" 
                          rel="noreferrer"
                        >
                          <span className="absolute -inset-x-4 -inset-y-2.5 hidden rounded md:-inset-x-6 md:-inset-y-4 lg:block"></span>
                          <span>Full-Stack Developer · <img src={echoLogo} alt="Echo" className="h-4 w-4 rounded-sm inline" /> <span>Echo</span></span>
                          <ExternalLink className="ml-1 inline-block h-3.5 w-3.5 shrink-0 translate-y-0.5 transition-transform group-hover/link:-translate-y-1 group-hover/link:translate-x-1" />
                        </a>
                      </div>
                      <div className="text-zinc-500 text-sm mt-1">No-Contact Breakup Wellness Startup</div>
                    </h3>
                    <p className="mt-2 text-sm leading-normal text-zinc-400">
                      Built and maintained a production Python/FastAPI and PostgreSQL backend for 1,000+ active users. Designed Supabase RLS schemas, Docker deployments on Railway, and implemented real-time LLM streaming through WebSockets. Owned Stripe-to-Dodo Payments migration.
                    </p>
                  </div>
                </div>

                {/* Job 3 */}
                <div className="group relative grid pb-1 transition-all sm:grid-cols-8 sm:gap-8 md:gap-4 pl-8">
                  <div className="absolute -inset-x-4 -inset-y-4 z-0 hidden rounded-md transition motion-reduce:transition-none lg:-inset-x-6 lg:block lg:group-hover:bg-zinc-800/50 lg:group-hover:shadow-[inset_0_1px_0_0_rgba(148,163,184,0.1)] lg:group-hover:drop-shadow-lg"></div>
                  <div className="absolute -left-[6.5px] mt-1.5 h-3 w-3 rounded-full border-2 border-indigo-500 bg-black z-10"></div>
                  <header className="z-10 mb-2 mt-1 text-xs font-semibold uppercase tracking-wide text-zinc-500 sm:col-span-2">
                    2025 — 2026
                  </header>
                  <div className="z-10 sm:col-span-6">
                    <h3 className="font-medium leading-snug text-zinc-200">
                      <div>
                        <a 
                          className="inline-flex items-center gap-1.5 text-base font-medium text-zinc-200 hover:text-indigo-300 transition-colors group/link cursor-pointer" 
                          href="https://www.deltaww.com/en-US/index" 
                          target="_blank" 
                          rel="noreferrer"
                        >
                          <span className="absolute -inset-x-4 -inset-y-2.5 hidden rounded md:-inset-x-6 md:-inset-y-4 lg:block"></span>
                          <span>Backend Developer · <img src={deltaLogo} alt="Delta Electronics" className="h-4 w-4 rounded-sm inline" /> <span>Delta Electronics</span></span>
                          <ExternalLink className="ml-1 inline-block h-3.5 w-3.5 shrink-0 translate-y-0.5 transition-transform group-hover/link:-translate-y-1 group-hover/link:translate-x-1" />
                        </a>
                      </div>
                    </h3>
                    <p className="mt-2 text-sm leading-normal text-zinc-400">
                      Built REST APIs and internal data-product services with Node.js, Express, and Bun. Developed a multi-provider LLM wrapper with Redis caching. Built ETL-style transformation pipelines.
                    </p>
                  </div>
                </div>

                {/* Job 4 */}
                <div className="group relative grid pb-1 transition-all sm:grid-cols-8 sm:gap-8 md:gap-4 pl-8">
                  <div className="absolute -inset-x-4 -inset-y-4 z-0 hidden rounded-md transition motion-reduce:transition-none lg:-inset-x-6 lg:block lg:group-hover:bg-zinc-800/50 lg:group-hover:shadow-[inset_0_1px_0_0_rgba(148,163,184,0.1)] lg:group-hover:drop-shadow-lg"></div>
                  <div className="absolute -left-[6.5px] mt-1.5 h-3 w-3 rounded-full border-2 border-indigo-500 bg-black z-10"></div>
                  <header className="z-10 mb-2 mt-1 text-xs font-semibold uppercase tracking-wide text-zinc-500 sm:col-span-2">
                    Project-based
                  </header>
                  <div className="z-10 sm:col-span-6">
                    <h3 className="font-medium leading-snug text-zinc-200">
                      <div>
                        <a 
                          className="inline-flex items-center gap-1.5 text-base font-medium text-zinc-200 hover:text-indigo-300 transition-colors group/link cursor-pointer" 
                          href="https://digitalnirman.vercel.app/" 
                          target="_blank" 
                          rel="noreferrer"
                        >
                          <span className="absolute -inset-x-4 -inset-y-2.5 hidden rounded md:-inset-x-6 md:-inset-y-4 lg:block"></span>
                          <span>SDE I - Backend · <img src={digitalNirmanLogo} alt="Digital Nirman" className="h-4 w-4 rounded-sm inline" /> <span>Digital Nirman</span></span>
                          <ExternalLink className="ml-1 inline-block h-3.5 w-3.5 shrink-0 translate-y-0.5 transition-transform group-hover/link:-translate-y-1 group-hover/link:translate-x-1" />
                        </a>
                      </div>
                      <div className="text-zinc-500 text-sm mt-1">School Management SaaS</div>
                    </h3>
                    <p className="mt-2 text-sm leading-normal text-zinc-400">
                      Built multi-tenant backend APIs and data models. Applied SOLID design principles across Node.js, React, Nginx, and Rust development.
                    </p>
                  </div>
                </div>

              </div>
            </section>

            {/* Projects Section */}
            <section id="projects" className="scroll-mt-16 md:scroll-mt-24">
              <div className="mb-8">
                <h2 className="text-xl font-bold uppercase tracking-widest text-white">Projects</h2>
              </div>
              
              <div className="group/list relative space-y-16 pl-6 border-l border-zinc-800 ml-3">
                
                {[
                  {
                    name: "Lemda - Terminal Agent",
                    desc: "Reached the top of a Terminal-Bench leaderboard through autonomous terminal execution and end-to-end task completion.",
                    link: "https://lemda.pages.dev",
                    logo: lamdaLogo
                  },
                  {
                    name: "Hermes / Clawbot Agent",
                    desc: "Built a memory-rich, full-stack agent with open-source Slack and WhatsApp integrations for persistent multi-channel workflows.",
                    link: "https://github.com/aadityakumarsah/hermes",
                    logo: hermesLogo
                  },
                  {
                    name: "Model Router",
                    desc: "Built a routing layer that selects the best model for each task using capability, latency, cost, and availability signals, with policy controls and fallbacks.",
                    link: "https://github.com/aadityakumarsah/model-router",
                    logo: modelRouterLogo
                  },
                  {
                   name: "Dhukuti Protocol",
                   desc: "A trustless rotating savings system (ROSCA) built on the Solana blockchain. Click to view the full protocol documentation & reference guide.",
                   link: "https://docs-dhukuti-protocol.vercel.app/",
                   logo: nagrikLogo
                  }
                ].map((project, i) => (
                  <div key={i} className="group relative grid gap-4 pb-1 transition-all sm:grid-cols-8 sm:gap-8 md:gap-4">
                    <div className="absolute -inset-x-4 -inset-y-4 z-0 hidden rounded-md transition motion-reduce:transition-none lg:-inset-x-6 lg:block lg:group-hover:bg-zinc-800/50 lg:group-hover:shadow-[inset_0_1px_0_0_rgba(148,163,184,0.1)] lg:group-hover:drop-shadow-lg"></div>
                    <div className="z-10 sm:col-span-8">
                      <h3 className="font-medium leading-snug text-zinc-200">
                        <a className="inline-flex items-center gap-2 font-medium leading-tight text-zinc-200 hover:text-indigo-300 focus-visible:text-indigo-300 group/link text-base" href={project.link} target="_blank" rel="noreferrer">
                          <span className="absolute -inset-x-4 -inset-y-2.5 hidden rounded md:-inset-x-6 md:-inset-y-4 lg:block"></span>
                          {project.logo && <img src={project.logo} alt="" className="h-5 w-5 rounded-sm" />}
                          <span>{project.name}</span>
                          <ExternalLink className="ml-1 inline-block h-3.5 w-3.5 shrink-0 translate-y-0.5 transition-transform group-hover/link:-translate-y-1 group-hover/link:translate-x-1" />
                        </a>
                      </h3>
                      <p className="mt-2 text-sm leading-normal text-zinc-400">
                        {project.desc}
                      </p>
                    </div>
                  </div>
                ))}

              </div>
            </section>

            {/* Achievements Section */}
            <section className="scroll-mt-16 md:scroll-mt-24">
               <div className="flex items-center gap-3 mb-6">
                <Trophy size={20} className="text-indigo-400" />
                <h2 className="text-lg font-bold text-white">Selected Achievements</h2>
              </div>
              <ul className="space-y-4 text-sm text-zinc-400 leading-relaxed">
                <li className="flex items-start gap-3">
                  <ChevronRight size={16} className="text-indigo-500 mt-1 shrink-0" />
                  <span>Winner, Health Theme - <img src={openaiLogo} alt="OpenAI" className="h-3.5 w-3.5 inline align-middle" /><span className="font-bold text-white ml-1">OpenAI</span>{' '}Build Week (50,000 participants).</span>
                </li>
                <li className="flex items-start gap-3">
                  <ChevronRight size={16} className="text-indigo-500 mt-1 shrink-0" />
                  <span>Winner, Nepal's biggest fintech <img src={esewaLogo} alt="eSewa" className="h-3.5 w-3.5 inline align-middle rounded-sm" /><span className="text-white font-bold ml-1">eSewa</span> x <img src={wwfLogo} alt="WWF" className="h-3.5 w-3.5 inline align-middle rounded-sm" /><span className="text-white font-bold ml-1">WWF</span> Hackathon</span>
                </li>
                <li className="flex items-start gap-3">
                  <ChevronRight size={16} className="text-indigo-500 mt-1 shrink-0" />
                  <span>Organizer, Daydream Biratnagar (<img src={hackClubLogo} alt="Hack Club" className="h-3.5 w-3.5 inline align-middle rounded-sm" /><span className="text-white font-bold ml-1">Hack Club</span>).</span>
                </li>
                <li className="flex items-start gap-3">
                  <ChevronRight size={16} className="text-indigo-500 mt-1.5 shrink-0" />
                  <div>
                    <span className="font-semibold text-zinc-200 block mb-2">Open-source contributor:</span>
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
                      {[
                        { logo: cogneeLogo, href: "https://cognee.ai", label: "Cognee" },
                        { logo: mastraLogo, href: "https://mastra.ai", label: "Mastra", yc: "W25" },
                        { logo: modelenceLogo, href: "https://modelence.com", label: "Modelence", yc: "S25" },
                        { logo: metaLogo, href: "https://opensource.fb.com", label: "Meta" },
                        { logo: openClawLogo, href: "https://openclaw.ai", label: "OpenClaw" },
                        { logo: insforgeLogo, href: "https://insforge.dev", label: "InsForge", yc: "P26" },
                        { logo: fossasiaLogo, href: "https://fossasia.org", label: "FOSS Asia" },
                        { logo: kiloCodeLogo, href: "https://kilo.ai", label: "KiloCode" },
                      ].map((contrib, i) => (
                        <ContribBadge
                          key={i}
                          idx={i}
                          logo={contrib.logo}
                          href={contrib.href}
                          label={contrib.label}
                          yc={contrib.yc}
                        />
                      ))}
                    </div>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <ChevronRight size={16} className="text-indigo-500 mt-1 shrink-0" />
                  <span><img src={ycLogo} alt="YC" className="h-3.5 w-3.5 inline align-middle" /><span className="font-bold text-[#FF6600] ml-1">Y Combinator</span> Startup School India - selected participant for founders building AI-era products.</span>
                </li>
              </ul>
            </section>

            {/* Education Section */}
            <section className="scroll-mt-16 md:scroll-mt-24">
               <div className="flex items-center gap-3 mb-6">
                <GraduationCap size={20} className="text-indigo-400" />
                <h2 className="text-lg font-bold text-white">Education</h2>
              </div>
              <div className="space-y-6 text-sm">
                <div className="group relative flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-4 -mx-4 rounded-xl transition-all hover:bg-zinc-800/50">
                  <div>
                    <h3 className="font-semibold text-zinc-200">Bachelor of Science in Data Science & AI</h3>
                    <p className="font-medium text-zinc-300 inline-flex items-center gap-1.5"><img src={bitsPilaniLogo} alt="BITS Pilani" className="h-4 w-4 rounded-sm" /> BITS Pilani</p>
                  </div>
                  <span className="text-zinc-500 font-medium bg-zinc-900/80 px-3 py-1 rounded-full text-xs border border-zinc-800">Started Aug 2026</span>
                </div>
                
                <div className="group relative flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-4 -mx-4 rounded-xl transition-all hover:bg-zinc-800/50">
                  <div className="z-10">
                    <h3 className="font-semibold text-zinc-200">
                      <a 
                        className="inline-flex items-center gap-1 font-semibold text-zinc-200 hover:text-indigo-300 transition-colors group/link cursor-pointer text-base" 
                        href="https://www.facebook.com/photo?fbid=1410878644400236&set=a.550394217115354" 
                        target="_blank" 
                        rel="noreferrer"
                      >
                        <span className="absolute -inset-x-4 -inset-y-2.5 hidden rounded md:-inset-x-6 md:-inset-y-4 lg:block"></span>
                        <span>High School (+2), Science Stream</span>
                        <ExternalLink className="ml-1 inline-block h-3.5 w-3.5 shrink-0 translate-y-0.5 transition-transform group-hover/link:-translate-y-1 group-hover/link:translate-x-1" />
                      </a>
                    </h3>
                    <p className="text-zinc-500 inline-flex items-center gap-1.5"><img src={arnikoLogo} alt="Arniko College" className="h-4 w-4 rounded-sm" /> Arniko College, Biratnagar, Nepal</p>
                  </div>
                  <span className="text-zinc-500 font-medium bg-zinc-900/80 px-3 py-1 rounded-full text-xs border border-zinc-800 z-10">Graduated 2026</span>
                </div>
              </div>

            </section>

            {/* GitHub Contribution */}
            <section className="scroll-mt-16 md:scroll-mt-24">
              <div className="flex items-center gap-3 mb-6">
                <GitBranch size={20} className="text-indigo-400" />
                <h2 className="text-lg font-bold text-white">Contribution</h2>
              </div>
              <a href="https://github.com/aadityakumarsah" target="_blank" rel="noreferrer" className="block p-3 rounded-xl border-2 border-zinc-700/50 hover:border-indigo-500/50 bg-zinc-900/30 transition-all duration-300">
                <img
                  src={githubContrib}
                  alt="GitHub Contribution"
                  className="w-full rounded-lg"
                />
              </a>
            </section>



          </main>
          
          {/* Footer Questions & Contacts */}
          <footer className="mt-16 pt-12 border-t border-zinc-800 flex flex-col gap-12 pb-16">
            <div>
              <div className="flex items-center gap-3 mb-6">
                <img src={questionIcon} alt="" className="h-7 w-7 rounded-md shrink-0" />
                <h2 className="text-xl font-bold uppercase tracking-widest text-white">
                  What I ask myself while building
                </h2>
              </div>
              <div className="space-y-3">
                <div className="group flex items-center gap-4 p-4 rounded-xl border border-zinc-800 bg-gradient-to-r from-zinc-900/40 to-zinc-900/10 hover:border-indigo-500/40 hover:from-indigo-500/10 transition-all duration-300">
                  <span className="h-9 w-9 shrink-0 flex items-center justify-center rounded-lg bg-indigo-500/10 border border-indigo-500/30 text-indigo-400 font-bold text-sm select-none transition-colors group-hover:bg-indigo-500/20 group-hover:text-indigo-300">1</span>
                  <span className="text-sm font-medium text-zinc-300 group-hover:text-zinc-100 transition-colors">What all services do you need (fe/be etc)?</span>
                </div>
                <div className="group flex items-center gap-4 p-4 rounded-xl border border-zinc-800 bg-gradient-to-r from-zinc-900/40 to-zinc-900/10 hover:border-indigo-500/40 hover:from-indigo-500/10 transition-all duration-300">
                  <span className="h-9 w-9 shrink-0 flex items-center justify-center rounded-lg bg-indigo-500/10 border border-indigo-500/30 text-indigo-400 font-bold text-sm select-none transition-colors group-hover:bg-indigo-500/20 group-hover:text-indigo-300">2</span>
                  <span className="text-sm font-medium text-zinc-300 group-hover:text-zinc-100 transition-colors">What happens if a server running your agent crashes?</span>
                </div>
                <div className="group flex items-center gap-4 p-4 rounded-xl border border-zinc-800 bg-gradient-to-r from-zinc-900/40 to-zinc-900/10 hover:border-indigo-500/40 hover:from-indigo-500/10 transition-all duration-300">
                  <span className="h-9 w-9 shrink-0 flex items-center justify-center rounded-lg bg-indigo-500/10 border border-indigo-500/30 text-indigo-400 font-bold text-sm select-none transition-colors group-hover:bg-indigo-500/20 group-hover:text-indigo-300">3</span>
                  <span className="text-sm font-medium text-zinc-300 group-hover:text-zinc-100 transition-colors">How would you do context management?</span>
                </div>
                <div className="group flex items-center gap-4 p-4 rounded-xl border border-zinc-800 bg-gradient-to-r from-zinc-900/40 to-zinc-900/10 hover:border-indigo-500/40 hover:from-indigo-500/10 transition-all duration-300">
                  <span className="h-9 w-9 shrink-0 flex items-center justify-center rounded-lg bg-indigo-500/10 border border-indigo-500/30 text-indigo-400 font-bold text-sm select-none transition-colors group-hover:bg-indigo-500/20 group-hover:text-indigo-300">4</span>
                  <span className="text-sm font-medium text-zinc-300 group-hover:text-zinc-100 transition-colors">How would you evaluate your agent?</span>
                </div>
                <div className="group flex items-center gap-4 p-4 rounded-xl border border-zinc-800 bg-gradient-to-r from-zinc-900/40 to-zinc-900/10 hover:border-indigo-500/40 hover:from-indigo-500/10 transition-all duration-300">
                  <span className="h-9 w-9 shrink-0 flex items-center justify-center rounded-lg bg-indigo-500/10 border border-indigo-500/30 text-indigo-400 font-bold text-sm select-none transition-colors group-hover:bg-indigo-500/20 group-hover:text-indigo-300">5</span>
                  <span className="text-sm font-medium text-zinc-300 group-hover:text-zinc-100 transition-colors">What metrics do you observe as your app grows?</span>
                </div>
              </div>
            </div>

            {videos.length > 0 && (
              <div className="w-full min-w-0">
                <div className="flex items-center gap-3 mb-6">
                  <CirclePlay size={20} className="text-indigo-400" />
                  <h2 className="text-xl font-bold uppercase tracking-widest text-white">
In my Free Time
                  </h2>
                </div>
                <VideosShowcase videos={videos} />
              </div>
            )}

            {/* Contacts & Socials Divider */}
            <div className="border-t border-zinc-800/80 pt-8 flex flex-col items-center gap-6">
              <div className="flex flex-wrap justify-center gap-8 text-zinc-400 text-sm font-medium">
                <a href="mailto:aadityakumarsah259@gmail.com" className="flex items-center gap-2 hover:text-white transition-colors">
                  <Mail size={18} />
                  <span>aadityakumarsah259@gmail.com</span>
                </a>
                <a href="https://wa.me/9779827068776" className="flex items-center gap-2 hover:text-white transition-colors" target="_blank" rel="noreferrer">
                  <Phone size={18} />
                  <span>+977 9827068776</span>
                </a>
                <div className="flex items-center gap-2 select-none">
                  <MapPin size={18} />
                  <span>Biratnagar, Nepal</span>
                </div>
              </div>
              <div className="flex flex-wrap justify-center gap-8 text-zinc-400 text-sm font-medium">
                <a href="https://github.com/aadityakumarsah" className="flex items-center gap-2 hover:text-white transition-colors" target="_blank" rel="noreferrer">
                  <GitBranch size={18} />
                  <span>GitHub</span>
                </a>
                <a href="https://www.linkedin.com/in/aaditya-sah-516178308" className="flex items-center gap-2 hover:text-white transition-colors" target="_blank" rel="noreferrer">
                  <Globe size={18} />
                  <span>LinkedIn</span>
                </a>
                <a href="https://x.com/aadityakumarsa" className="flex items-center gap-2 hover:text-white transition-colors" target="_blank" rel="noreferrer">
                  <svg viewBox="0 0 24 24" width="18" height="18" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-twitter"><path d="M22 4s-.7 2.1-2 3.4c1.6 10-9.4 17.3-18 11.6 2.2.1 4.4-.6 6-2C3 15.5.5 9.6 3 5c2.2 2.6 5.6 4.1 9 4-.9-4.2 4-6.6 7-3.8 1.1 0 3-1.2 3-1.2z"/></svg>
                  <span>X</span>
                </a>
                <a href="https://medium.com/@aadityakumarsa" className="flex items-center gap-2 hover:text-white transition-colors" target="_blank" rel="noreferrer">
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" className="lucide lucide-medium"><path d="M13.54 12a6.8 6.8 0 0 1-6.77 6.82A6.8 6.8 0 0 1 0 12a6.8 6.8 0 0 1 6.77-6.82A6.8 6.8 0 0 1 13.54 12zM20.96 12c0 3.54-1.51 6.42-3.38 6.42-1.87 0-3.39-2.88-3.39-6.42s1.52-6.42 3.39-6.42 3.38 2.88 3.38 6.42zM24 12c0 3.17-.53 5.75-1.19 5.75-.66 0-1.19-2.58-1.19-5.75s.53-5.75 1.19-5.75C23.47 6.25 24 8.83 24 12z"/></svg>
                  <span>Medium</span>
                </a>
              </div>
            </div>
          </footer>
        </div>

        </div>
      </div>
    </div>


    </>
  );
}

function VideosShowcase({ videos }: { videos: PortfolioVideo[] }) {
  const [selected, setSelected] = useState<PortfolioVideo | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const railRef = useRef<HTMLDivElement>(null);
  const descRef = useRef<HTMLParagraphElement>(null);
  const dragRef = useRef<{ startX: number; startLeft: number } | null>(null);
  const suppressClickRef = useRef(false);

  useEffect(() => {
    if (!selected) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setSelected(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selected]);

  useEffect(() => {
    setExpanded(false);
    setHasMore(false);
    const id = requestAnimationFrame(() => {
      const el = descRef.current;
      if (el) setHasMore(el.scrollHeight > el.clientHeight + 1);
    });
    return () => cancelAnimationFrame(id);
  }, [selected]);

  const items = videos.filter(v => extractYouTubeId(v.destinationUrl ?? ""));
  if (items.length === 0) return null;

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    suppressClickRef.current = false;
    const rail = railRef.current;
    if (!rail) return;
    dragRef.current = { startX: e.clientX, startLeft: rail.scrollLeft };
  };
  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const rail = railRef.current, drag = dragRef.current;
    if (!rail || !drag) return;
    const dx = e.clientX - drag.startX;
    if (Math.abs(dx) > 6) {
      suppressClickRef.current = true;
      rail.scrollLeft = drag.startLeft - dx;
    }
  };
  const onPointerUp = () => { dragRef.current = null; };
  const handleSelect = (video: PortfolioVideo) => (e: React.MouseEvent) => {
    if (suppressClickRef.current) { e.preventDefault(); suppressClickRef.current = false; return; }
    setExpanded(false);
    setSelected(video);
  };

  return (
    <div className="min-w-0">
      <div
        ref={railRef}
        className="no-scrollbar rail-fade flex w-full max-w-full cursor-grab touch-pan-x gap-4 overflow-x-auto pb-2 select-none active:cursor-grabbing"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        {items.map(video => (
          <button
            key={video.id}
            type="button"
            onClick={handleSelect(video)}
            className="group w-64 md:w-72 shrink-0 cursor-pointer text-left focus:outline-none"
          >
            <span className="relative block overflow-hidden rounded-xl border border-zinc-800 bg-zinc-900 transition-all duration-300 group-hover:border-indigo-500/50 group-hover:shadow-[0_0_24px_-6px_rgba(99,102,241,0.45)]">
              <img
                src={youtubeThumb(video.destinationUrl ?? "")}
                alt={video.title || "YouTube video"}
                loading="lazy"
                className="aspect-video w-full object-cover"
              />
              <span className="absolute inset-0 grid place-items-center bg-gradient-to-t from-black/70 via-black/10 to-transparent">
                <CirclePlay size={44} className="text-white/90 drop-shadow-lg transition-transform duration-300 group-hover:scale-110" />
              </span>
            </span>
            {video.title && <span className="mt-2.5 block w-full min-w-0 truncate text-sm font-medium text-zinc-300 transition-colors group-hover:text-white">{video.title}</span>}
          </button>
        ))}
      </div>

      {selected && (() => {
        const id = extractYouTubeId(selected.destinationUrl ?? "");
        if (!id) return null;
        return (
          <div
            className="fixed inset-0 z-[60] flex items-center justify-center overflow-y-auto bg-black/80 p-4 backdrop-blur-sm sm:p-6"
            onClick={() => setSelected(null)}
            role="dialog"
            aria-modal="true"
          >
            <div className="w-full max-w-3xl overflow-hidden rounded-2xl border border-zinc-700/60 bg-zinc-900/95 shadow-2xl" onClick={e => e.stopPropagation()}>
              <div className="relative aspect-video w-full">
                <iframe
                  src={`${youtubeEmbed(selected.destinationUrl ?? "")}?autoplay=1&rel=0`}
                  title={selected.title || "YouTube video"}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  className="absolute inset-0 h-full w-full"
                />
              </div>
              <div className="p-4 sm:p-5">
                {selected.title && <p className="break-words font-semibold text-white">{selected.title}</p>}
                {selected.description && (
                  <div className="mt-3">
                    {expanded ? (
                      <div className="scrollbar-videos max-h-40 overflow-y-auto break-words rounded-lg border border-zinc-800 bg-black/30 p-3">
                        <p className="text-sm leading-relaxed whitespace-pre-wrap text-zinc-400">{selected.description}</p>
                      </div>
                    ) : (
                      <p ref={descRef} className="break-words text-sm leading-relaxed whitespace-pre-wrap line-clamp-6 text-zinc-400">{selected.description}</p>
                    )}
                    {hasMore && (
                      <button
                        type="button"
                        onClick={() => setExpanded(v => !v)}
                        className="mt-1.5 inline-flex items-center gap-1 text-xs font-semibold text-indigo-400 transition-colors hover:text-indigo-300"
                      >
                        {expanded ? "See less" : "See more"}
                        <ChevronDown size={14} className={`transition-transform duration-200 ${expanded ? "rotate-180" : ""}`} />
                      </button>
                    )}
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => setSelected(null)}
                  className="mt-4 inline-flex items-center gap-1.5 rounded-lg border border-zinc-700 px-3 py-1.5 text-sm text-zinc-300 transition-colors hover:border-indigo-500/50 hover:text-white"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

export default App;